import { useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useOnboardingStore } from '../model/onboarding-store';
import { useDismissTourMutation } from '../api/onboarding.api';

/**
 * Hook to read and write onboarding tour state.
 * Combines localStorage (via Zustand persist) with backend persistence.
 */
export function useOnboardingState() {
  const { tourStates, activeTourId, startTour, dismissTour, stopActiveTour } = useOnboardingStore(
    useShallow((state) => ({
      tourStates: state.tourStates,
      activeTourId: state.activeTourId,
      startTour: state.startTour,
      dismissTour: state.dismissTour,
      stopActiveTour: state.stopActiveTour,
    })),
  );

  const { dismissTour: dismissTourOnServer } = useDismissTourMutation();

  const isTourDismissed = useCallback(
    (tourId: string): boolean => {
      return tourStates[tourId]?.dismissed ?? false;
    },
    [tourStates],
  );

  const handleDismissTour = useCallback(
    (tourId: string) => {
      // Save locally immediately (via Zustand persist -> localStorage)
      dismissTour(tourId);
      // Save to backend (fire-and-forget)
      dismissTourOnServer(tourId);
    },
    [dismissTour, dismissTourOnServer],
  );

  return {
    tourStates,
    activeTourId,
    startTour,
    dismissTour: handleDismissTour,
    stopActiveTour,
    isTourDismissed,
  };
}
